<template>

    <div class="catalog-component">

        <section class="container title solid">
            <h1>{{ title.toUpperCase() }}</h1>
        </section>

        <section v-if="text" class="container title-text solid d-none d-sm-block">
            <transition-expand>
            <p v-show="text" class="slab mb-0">{{ text }}</p>
            </transition-expand>
        </section>

        <section class="spacer-3x d-none d-sm-block" />

        <div class="container bg-white py-4">

            <Breadcrumbs :category="category" :subcategory="subcategory" />

            <div class="d-sm-none">
                <CatalogSorting :count="count" :sorts="sorts" :sort="sort" :mobile="true" @sort-change="onSortChange" />
            </div>

            <section class="content">

                <div class="row">

                    <div class="col-sm-4 col-md-3">

                        <CatalogSidebar :category="category" :categories="categoryList" :counts="counts" :filters="filters" @filter-reset="onFilterReset">
                            <template v-slot:filters>
                                <CatalogFilter
                                    v-for="(filter, key) in filters"
                                    :open="key == 'brands' && brandSlug != null"
                                    :key="key"
                                    :name="key"
                                    :filter="filter"
                                    @filter-change="onFilterChange"
                                />
                            </template>
                        </CatalogSidebar>

                        <!-- Overlay to close filter mobile -->
                        <a class="catalog-filter-close" href="#" v-b-toggle.catalog-filters></a>

                    </div>

                    <div class="col-sm-8 col-md-9">
                        <!-- Category Grid! -->
                        <transition name="fade">
                        <section v-if="!category && !subcategory" class="content-category-grid d-none d-md-block" >
                            <CategoryGrid :categories="categories" />
                        </section>
                        <section v-else-if="!subcategory" class="content-category-grid d-none d-md-block" >
                            <CategoryGrid :category="category" />
                        </section>
                        </transition>

                        <div class="d-none d-sm-block">
                            <CatalogSorting :count="count" :sorts="sorts" :sort="sort" @sort-change="onSortChange" />
                        </div>

                        <transition name="fade" mode="out-in">
                        <div v-if="loading" class="loading">
                            <div class="spinner-grow text-dark" role="status"></div>
                        </div>
                        <ProductGrid v-else :products="products" :page="page" :pages="pages" />
                        </transition>

                    </div>
                </div>
            </section>
        </div>

    </div>

</template>

<script>

    import Breadcrumbs from '@/components/Breadcrumbs.vue';
    import CatalogSidebar from '@/components/CatalogSidebar.vue';
    import CatalogSorting from '@/components/CatalogSorting.vue';
    import CatalogFilter from '@/components/CatalogFilter.vue'
    import CategoryGrid from '@/components/CategoryGrid.vue';
    import ProductGrid from '@/components/ProductGrid.vue'
    import TransitionExpand from '@/components/TransitionExpand.vue'
    import {HTTP} from '../http-common';
    import qs from 'qs';

    export default {

        name: 'catalog',

        props: {
            brands: Array,
            categories: Array,
            sorts: Object,
            // slugs
            categorySlug: String,
            subcategorySlug: String,
            brandSlug: String,
            // prefilter options
            prefilteredProducts: Array,
            prefilteredCounts: Object,
            prefilteredPages: Number,
            initialFilters: Object,
            initialActiveFilters: Object,
        },

        data: function() {
            return {
                loading: false,
                products: [...this.prefilteredProducts],
                errors: [],
                count: this.prefilteredProducts.length,
                counts: {...this.prefilteredCounts},
                page: parseInt(this.$route.query.page) || 1,
                pages: this.prefilteredPages,
                sort: Object.keys(this.sorts)[0],
                filters: this.initialFilters,
            }
        },

        computed: {

            title: function() {
                if (this.brand)
                    return this.brand.name;
                else if (this.subcategory)
                    return this.subcategory.name;
                else if (this.category)
                    return this.category.name;
                else
                    return "Product Catalogue";
            },

            text: function() {
                if (this.brand)
                    return this.brand.description;
                else if (this.subcategory)
                    return this.subcategory.description;
                else if (this.category)
                    return this.category.description;
                else
                    return "Explore our catalogue and discover our entire selection of gourmet foods and brands from Italy, Europe and the rest of the world.";
            },

            brand: function() {
                return _.find(this.brands, {'slug': this.brandSlug}) || null;
            },

            category: function() {
                return _.find(this.categories, {'slug': this.categorySlug}) || null;
            },

            subcategory: function() {
                if (this.category)
                    return _.find(this.category.subcategories, {'slug': this.subcategorySlug});
                else
                    return null;
            },

            hero: function() {
                if (this.brand && this.brand.photo) {
                    return this.brand.photo;
                } else if (this.category && this.category.photo) {
                    return this.category.photo
                } else {
                    return null;
                }
            },

            categoryList: function() {
                if (this.category) {
                    // let obj = Object();
                    // obj[this.category.id] = this.category;
                    return [this.category];
                } else {
                    return this.categories;
                }
            },

            selectedCategorySlug: function() {
                if (this.subcategory)
                    return this.subcategory.slug;
                else if (this.category)
                    return this.category.slug;
                else
                    return null;
            },
        },

        created: function() {
            console.log('Catalog created.');
            this.setActiveFilters(this.initialActiveFilters);
            if (!this.products.length) {
                this.fetch();
            }
        },

        watch: {
            '$route' (to, from) {
                // console.log(to, from)
                if (to.name != from.name) {
                    this.onCategoryChange();
                } else {
                    this.onPageChange();
                }
            }
        },

        methods: {

            getSelectedOptions: function(name) {
                let options = this.filters[name].options.filter(opt => opt.on);
                return options.map(opt => (opt.id));
            },

            // fetch products from server
            fetch: function() {

                console.log('Grabbing products from server...');
                this.loading = true;

                HTTP.get('products/', {
                    params: {
                        category: this.selectedCategorySlug,
                        brands: this.getSelectedOptions("brands"),
                        ranges: this.getSelectedOptions("ranges"),
                        regions: this.getSelectedOptions("regions"),
                        characteristics: this.getSelectedOptions("characteristics"),
                        tags: this.getSelectedOptions("tags"),
                        page: this.$route.query.page,
                        sort: this.sort,
                    },
                    paramsSerializer: function(params) {
                        return qs.stringify(params, { indices: false })
                    }
                })
                .then(response => {
                    console.log('Grabbed', response.data.products.length, 'products.')
                    this.products = response.data.products;
                    this.counts = response.data.counts;
                    this.count = response.data.count;
                    this.page = response.data.page;
                    this.pages = response.data.pages;
                    this.sort = response.data.sort;
                    this.setActiveFilters(response.data.active_filters);

                })
                .catch(e => {
                    console.log(e);
                    this.errors.push(e);
                })
                .finally(() => {
                    this.loading = false;
                });
            },

            setActiveFilters: function(data) {
                for (let f in this.filters) {
                    for (let opt of this.filters[f].options) {
                        this.$set(opt, 'active', data[f].indexOf(opt.id) != -1);
                    }
                }
            },

            clearFilters: function() {
                // TODO: Looks like we're modifying a prop here.
                // Should probably change that.
                for (let f in this.filters) {
                    for (let opt of this.filters[f].options) {
                        this.$set(opt, 'on', false);
                    }
                }
            },

            onFilterChange: function(filter, optionId, checked) {
                // TODO: Looks like we're modifying a prop here.
                // Should probably change that.
                let f = _.find(this.filters[filter].options, {id: optionId})
                this.$set(f, 'on', checked);
                this.$router.push({query: {}}, null, this.fetch);
            },

            onFilterReset: function() {
                this.clearFilters();
                this.$router.push({name: 'home'}, null, this.fetch);
            },

            onSortChange: function(sort) {
                this.sort = sort;
                this.$router.push({query: {}}, null, this.fetch);
            },

            onCategoryChange: function() {
                this.clearFilters();
                this.updateHero();
                this.fetch();
            },

            onPageChange: function(page) {
                this.fetch();
            },

            updateHero: function() {
                let hero = document.getElementById('hero');
                let fallback = hero.dataset.fallback;
                console.log(this.hero);
                if (this.hero)
                    hero.style.backgroundImage = 'url(' + this.hero + ')';
                else
                    hero.style.backgroundImage = 'url(' + fallback + ')';

            }
        },

        components: {
            Breadcrumbs,
            CatalogSidebar,
            CatalogSorting,
            CatalogFilter,
            CategoryGrid,
            ProductGrid,
            TransitionExpand,
        },
    }

</script>


<style lang="scss" scoped>

    .loading {
        height: 100%;
    }

    .spinner-grow {
        width: 5rem;
        height: 5rem;
    }

</style>
